import { MessageFragment } from '../../types/messageFragment';
import { GroupedMessageResult } from '../../types/groupedMessageResult';
import { toHex } from './toHex';

function concatUint8Arrays(arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((acc, curr) => acc + curr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

/**
 * Groups fragments by messageId and assembles completed messages.
 * Removes completed entries from the fragment map after assembly.
 *
 * @param fragmentMap - Map of messageId → fragment list
 * @returns Array of grouped message results, including both complete and incomplete messages
 */
export function groupMessageFragmentsInPlace(
  fragmentMap: Map<string, MessageFragment[]>
): GroupedMessageResult[] {
  const groupedResults: GroupedMessageResult[] = [];

  for (const [messageId, frags] of fragmentMap.entries()) {
    if (!frags || frags.length === 0) continue;

    const total = frags[0].totalFragments;
    const messageType = frags[0].messageType;

    // Deduplicate fragments by fragmentIndex (keep first seen)
    const dedupedMap = new Map<number, MessageFragment>();
    for (const frag of frags) {
      if (!dedupedMap.has(frag.fragmentIndex)) {
        dedupedMap.set(frag.fragmentIndex, frag);
      }
    }

    const ordered = Array.from(dedupedMap.values()).sort((a, b) => a.fragmentIndex - b.fragmentIndex);
    const isComplete = ordered.length === total;

    const payload = isComplete
      ? concatUint8Arrays(ordered.map(f => f.payload))
      : undefined;

    let text: string | undefined;
    if (isComplete && messageType === 0) {
      try {
        text = new TextDecoder().decode(payload!);
      } catch (err) {
        console.warn('UTF-8 decode failed for messageId:', messageId, err);
        text = undefined;
      }
    }

    groupedResults.push({
      messageId,
      fragments: ordered,
      receivedFragments: dedupedMap.size,
      totalFragments: total,
      isComplete,
      messageType,
      assembledPayload: payload,
      text,
    });

    if (isComplete) {
      fragmentMap.delete(messageId);
    }
  }

  return groupedResults;
}